import {useEffect, useState} from "react";

import {useViewer} from "../context/ViewerContext";
import {evaluateFlag} from "../utils/flags";

/**
 * LaunchDarkly JSON flag driving the revamped profile listing in the main app
 * (see ProfileListingVirtualizedConfigSchema in @buildwithflux/app-flags-schema).
 * Its `virtualization`/`customScrollbar` controls also gate the virtualized
 * search results listing here.
 */
const FLAG_KEY = "profile-listing-virtualized";

export interface SearchListingConfig {
    /** Full-span virtualized results listing replacing paged navigation. */
    virtualization: boolean;
    /** List-aligned browser scrollbar; only effective with virtualization. */
    customScrollbar: boolean;
}

const defaultConfig: SearchListingConfig = {virtualization: false, customScrollbar: false};

function normalize(value: unknown): SearchListingConfig {
    if (typeof value !== "object" || value === null) {
        return defaultConfig;
    }
    const flag = value as {virtualization?: unknown; customScrollbar?: unknown};
    const virtualization = flag.virtualization === true;
    return {
        virtualization,
        customScrollbar: virtualization && flag.customScrollbar === true,
    };
}

/**
 * The viewer's virtualized-search-listing configuration. Defaults to all-off
 * (the classic paged listing): SSR and the first client render always use the
 * classic listing, and the flag is evaluated client-side once auth resolves.
 */
export function useSearchListingConfig(): SearchListingConfig {
    const viewer = useViewer();
    const {uid, email, handle, authResolved} = viewer;
    const identityKey = `${uid ?? ""}\0${email ?? ""}\0${handle ?? ""}`;
    const [config, setConfig] = useState(defaultConfig);
    // Reset during render on identity change (not in the effect), so a prior
    // viewer's configuration is never observable for even one committed render.
    const [lastIdentityKey, setLastIdentityKey] = useState(identityKey);
    if (identityKey !== lastIdentityKey) {
        setLastIdentityKey(identityKey);
        setConfig(defaultConfig);
    }

    useEffect(() => {
        if (!authResolved) {
            return undefined;
        }
        let cancelled = false;
        void evaluateFlag(FLAG_KEY, viewer)
            .then((value) => {
                if (!cancelled) {
                    setConfig(normalize(value));
                }
            })
            .catch(() => {
                // Keep the classic listing when evaluation fails
            });
        return () => {
            cancelled = true;
        };
    }, [authResolved, viewer]);

    return config;
}
